/**
 * Event Bus
 * Phase 6: Interaction Layer - Event System
 * 
 * Provides a centralized event handling system for the application.
 * Events can be subscribed, unsubscribed, and emitted.
 */

/**
 * Event handler function type
 */
export type EventHandler = (data: any) => void

/**
 * Event Bus
 * Manages event subscriptions and emissions
 */
export class EventBus {
  private listeners: Map<string, Set<EventHandler>> = new Map()

  /**
   * Subscribe to an event
   */
  on(event: string, handler: EventHandler): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(handler)
  }

  /**
   * Unsubscribe from an event
   */
  off(event: string, handler: EventHandler): void {
    const listeners = this.listeners.get(event)
    if (listeners) {
      listeners.delete(handler)
      if (listeners.size === 0) {
        this.listeners.delete(event)
      }
    }
  }

  /**
   * Emit an event to all subscribers
   */
  emit(event: string, data: any): void {
    const listeners = this.listeners.get(event)
    if (listeners) {
      // Create a copy to avoid issues if handlers modify the set
      const listenersCopy = new Set(listeners)
      listenersCopy.forEach(handler => handler(data))
    }
  }

  /**
   * Get the number of listeners for an event
   */
  getListenerCount(event: string): number {
    return this.listeners.get(event)?.size || 0
  }
}

// Create a singleton instance for use across the application
export const eventBus = new EventBus()
